import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { Layout } from '@components/Layout';
import { LoadingSpinner } from '@components/LoadingSpinner';
import { Card } from '@components/Card';
import { getBlogPosts } from '@lib/firebase/blog';
import { format } from 'date-fns';

/**
 * Blog listing page
 * Displays all published blog posts
 */
export function BlogPage(): JSX.Element {
  const { data: posts, isLoading } = useQuery({
    queryKey: ['blog-posts'],
    queryFn: () => getBlogPosts(),
  });

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <LoadingSpinner size="lg" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-extrabold text-gray-900 dark:text-white mb-4">
            Blog
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Stories, tips, and insights about e-bikes and urban mobility
          </p>
        </div>

        {/* Blog Posts Grid */}
        {posts && posts.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post: any) => (
              <Card key={post.id} className="overflow-hidden hover:shadow-xl transition-shadow">
                <Link to={`/blog/${post.slug}`}>
                  {post.featuredImage && (
                    <img
                      src={post.featuredImage}
                      alt={post.title}
                      className="w-full h-48 object-cover"
                    />
                  )}
                  <div className="p-6">
                    {post.category && (
                      <span className="inline-block px-3 py-1 text-xs font-semibold text-purple-600 dark:text-purple-400 bg-purple-100 dark:bg-purple-900/30 rounded-full mb-3">
                        {post.category}
                      </span>
                    )}
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2 line-clamp-2">
                      {post.title}
                    </h2>
                    {post.excerpt && (
                      <p className="text-gray-600 dark:text-gray-400 mb-4 line-clamp-3">
                        {post.excerpt.replace(/[#*_`]/g, '').trim()}
                      </p>
                    )}
                    <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                      {post.publishedAt && (
                        <time dateTime={post.publishedAt.toISOString()}>
                          {format(post.publishedAt, 'MMM d, yyyy')}
                        </time>
                      )}
                      {post.authorName && (
                        <span>By {post.authorName}</span>
                      )}
                    </div>
                  </div>
                </Link>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-600 dark:text-gray-400 text-lg">
              No blog posts yet. Check back soon!
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
}

